import { useCallback, useContext, useEffect, useRef, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { PlayersService } from "@/core/http/services/PlayersService";

const useUserBase = () => {
  const { userLoggedData, updateUserFromPlayer, updateAuthPatch } =
    useContext(AuthContext);

  const lastDashboardFetchRef = useRef({ playerId: null, at: 0 });
  const inFlightDashboardRef = useRef(false);

  const [loading, setLoading] = useState({
    dashboard: false,
  });
  const [error, setError] = useState({ type: null, message: null });

  const getDashboard = useCallback(async () => {
    const playerId = userLoggedData?.playerId;

    if (!playerId) {
      return;
    }

    const now = Date.now();
    if (
      inFlightDashboardRef.current ||
      (lastDashboardFetchRef.current.playerId === playerId &&
        now - lastDashboardFetchRef.current.at < 5000)
    ) {
      return;
    }

    inFlightDashboardRef.current = true;
    lastDashboardFetchRef.current = { playerId, at: now };

    setLoading((prev) => ({ ...prev, dashboard: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } = await PlayersService.getDashboard(playerId);
      if (data?.error || status !== 200) {
        setError({
          type: "dashboard",
          message: message || "Erro ao buscar os dados do dashboard.",
        });
        return;
      }

      const player = data?.player || null;
      const clubs = Array.isArray(player?.clubs) ? player.clubs : [];
      if (player) {
        await updateUserFromPlayer(player);
      }

      if (clubs.length > 0) {
        await updateAuthPatch({ clubs });
        if (!userLoggedData?.selectedClubId && clubs.length === 1 && clubs[0]?.id) {
          await updateAuthPatch({ selectedClubId: clubs[0].id });
        }
      }
    } catch (err) {
      setError({
        type: "dashboard",
        message: err?.message || "Erro inesperado ao buscar os dados do dashboard.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, dashboard: false }));
      inFlightDashboardRef.current = false;
    }
  }, [updateAuthPatch, updateUserFromPlayer, userLoggedData?.playerId]);

  useEffect(() => {
    getDashboard();
  }, [getDashboard, userLoggedData?.playerId]);

  const refreshPlayerData = useCallback(async () => {
    const playerId = userLoggedData?.playerId;
    if (!playerId) return;

    const { status, data } = await PlayersService.getById(playerId);
    if (status === 200 && data) {
      await updateUserFromPlayer(data);
    }
  }, [userLoggedData?.playerId, updateUserFromPlayer]);

  return {
    userLoggedData,
    loading,
    error,
    refreshPlayerData,
  };
};

export default useUserBase;
